import currency from "currency.js";
import { IConfirmationButton, IConfirmationModalStat } from ".";
import { GetAccount200ResponsePositionsInner, SideResponse } from "../../codegen-api";
import { COLORS } from "../../constants/design/colors";
import { AccountStateEnum } from "../../contexts/AuthContext";
import { ButtonThemeEnum } from "../Buttons/styles";
import { formatKnownPrice } from "../../utils/unknownNumbers";
import { floorSizeForDisplay } from "../../utils/format";

export function buildStats(
  position: GetAccount200ResponsePositionsInner | undefined,
  asset: string | undefined,
  pricePrecision: number,
  markPrice: any,
  amountPrecision: number,
  /** The market's size step; defaults to one unit of `amountPrecision`. */
  amountStep?: number
): IConfirmationModalStat[] {
  if (!position) {
    return [];
  }

  const items: IConfirmationModalStat[] = [
    {
      title: "Coin",
      data: asset || position.asset,
    },
    {
      title: "Position",
      data: (
        <span
          style={{
            color:
              position.side === SideResponse.Buy
                ? COLORS.up.one
                : COLORS.down.one,
          }}
        >
          {/* Floored: `toFixed` rounded the size up past the position. */}
          {floorSizeForDisplay(
            position.amount,
            amountStep && amountStep > 0 ? amountStep : 10 ** -amountPrecision,
            amountPrecision,
            "-"
          )}{" "}
          {asset || position.asset}
        </span>
      ),
    },
    {
      title: "Entry Price",
      data: currency(position.avg_entry_price || 0, {
        precision: pricePrecision,
      }).format(),
    },
    {
      title: "Mark Price",
      // Unknown mark: "-", never "$0.00".
      data: formatKnownPrice(markPrice, pricePrecision),
    },
  ];

  return items;
}

export function buildConfirmationButtons({
  accountSigningKeyState,
  t,
  setShowConnectModal,
  errors,
  onConfirmTPSLForPosition,
}: {
  accountSigningKeyState: any;
  t: any;
  setShowConnectModal: any;
  errors: any;
  onConfirmTPSLForPosition: () => void;
}): IConfirmationButton[] {
  const buttons: IConfirmationButton[] = [];
  // Only a MISSING WALLET blocks TP/SL.
  //
  // This gated on `!== OK`, which also catches REQUIRE_REGISTER_SIGNING and
  // REQUIRE_PASSWORD — the one-click agent-key states. TP/SL does not use that key:
  // the executor recovers the signer and compares it to `trader`, so the CONNECTED
  // wallet signs (see hooks/tpsl/useTpslApi), and the /tpsl proxy takes no API auth
  // at all — the EIP-712 signature in the body is the only credential. So a user who
  // simply hadn't registered a signing key, or whose key was locked, was sent to the
  // wallet modal instead of being allowed to set a stop-loss they were fully able to
  // sign for.
  if (accountSigningKeyState === AccountStateEnum.REQUIRE_CONNECT) {
    buttons.push({
      title: t("complete_sign_in"),
      onClick: () => setShowConnectModal(true),
      theme: ButtonThemeEnum.HIGHLIGHT,
    });
  } else {
    buttons.push({
      title: "Confirm",
      theme: ButtonThemeEnum.HIGHLIGHT,
      disabled:
        Boolean(errors?.tpTriggerPrice?.type) ||
        Boolean(errors?.slTriggerPrice?.type) ||
        Boolean(errors?.tpRoi?.type) ||
        Boolean(errors?.slRoi?.type) ||
        // Advanced fields. Their validators self-disable when the matching
        // checkbox is off, so these can never latch a hidden input.
        Boolean(errors?.tpslCloseSize?.type) ||
        Boolean(errors?.tpLimitPrice?.type) ||
        Boolean(errors?.slLimitPrice?.type),
      onClick: onConfirmTPSLForPosition,
    });
  }

  return buttons;
}
